import {
	Button,
	Divider,
	Dropzone,
	Form,
	Header,
	type Job,
	Label,
	Message,
	Segment,
} from '@mairistem/composants';
import { useService } from '@mairistem/comptabilite';
import { Icon } from '@mairistem/finances-utils';
import React from 'react';
import StorageKey from '../../constants/common/storageKey';
import { TAILLE_MAX, TYPES_ACCEPTES } from '../../constants/pes/importPes';
import useTraitementLong from '../../hooks/useTraitementLong';
import {
	analyserImportPes,
	type CompteRenduImportPes,
	type FluxPesEnAttente,
	getFluxPesEnAttente,
	importerPes,
	type SourceImportPes,
} from '../../services/pes/importPes';
import getMessageErreur from '../../utils/getMessageErreur';
import { libelleFlux } from '../../utils/pes/fluxEnAttente';
import { pluriel, taille } from '../../utils/pes/format';
import CompteRenduImport from './CompteRenduImport';
import FluxEnAttenteModal from './FluxEnAttenteModal';

/**
 * Dropzone JVS avec son vrai type : ses typages, générés depuis les propTypes, déclarent onDrop null alors qu'il reçoit
 * les fichiers déposés ; les autres propriétés vont à react-dropzone (maxSize, onDropRejected).
 */
const ZoneDepot = Dropzone as unknown as React.ComponentType<{
	accept: Record<string, string[]>;
	maxSize: number;
	disabled: boolean;
	tooltip: null;
	onDrop: (fichiers: File[]) => void;
	onDropRejected: (
		rejets: { file: File; errors: { code: string }[] }[],
	) => void;
}>;

/** Pourquoi react-dropzone a refusé le fichier déposé, dit à l'utilisateur. */
const motifRefus = (rejet: { file: File; errors: { code: string }[] }) =>
	rejet.errors.some((erreur) => erreur.code === 'file-too-large')
		? `Le fichier ${rejet.file.name} (${taille(rejet.file.size)}) dépasse ${taille(TAILLE_MAX)}.`
		: `Le fichier ${rejet.file.name} n'est pas un fichier PES : seuls les .xml et les .zip sont acceptés.`;

const lireCompteRendu = (job: Job): CompteRenduImportPes =>
	JSON.parse(job.resultat) as CompteRenduImportPes;

/** Nom de la source dans la page et le compte rendu (export CSV compris). */
const nomSource = (source: SourceImportPes) =>
	source instanceof File ? source.name : libelleFlux(source);

/**
 * Import d'un flux PES (aller ou retour, XML ou zip) : dépôt d'un fichier, ou choix d'un flux déposé par un logiciel
 * métier (FluxEnAttenteModal), et analyse (rien n'est écrit), puis import confirmé, tout ou rien. Chaque étape est un
 * Job360 dont le résultat est le compte rendu, présenté par CompteRenduImport.
 */
const ImportPes = () => {
	const identifiantExercice = Number(
		sessionStorage.getItem(StorageKey.IDENTIFIANT_EXERCICE),
	);
	const [ServiceCombobox, isServiceVisible] = useService();
	const [source, setSource] = React.useState<SourceImportPes | null>(null);
	const [refus, setRefus] = React.useState<string | null>(null);
	const [enAttente, setEnAttente] = React.useState<FluxPesEnAttente[]>([]);
	const [chargementEnAttente, setChargementEnAttente] = React.useState(false);
	const [erreurEnAttente, setErreurEnAttente] = React.useState<string | null>(
		null,
	);
	const [choixOuvert, setChoixOuvert] = React.useState(false);
	const [service, setService] = React.useState<number | undefined>();
	const [cleFichier, setCleFichier] = React.useState<string | null>(null);
	const [analyse, setAnalyse] = React.useState<CompteRenduImportPes | null>(
		null,
	);
	const [import_, setImport] = React.useState<CompteRenduImportPes | null>(
		null,
	);
	// rang du dernier chargement de la liste : une réponse plus ancienne, arrivée après, est ignorée
	const dernierChargement = React.useRef(0);
	// exercice du contexte (paramètre de l'URL) : sans lui, rien à lister ni à importer
	const avecExercice =
		Number.isInteger(identifiantExercice) && identifiantExercice > 0;

	const suiviAnalyse = useTraitementLong(
		'Analyse du fichier PES',
		"L'analyse du fichier a échoué",
		async (job) => setAnalyse(lireCompteRendu(job)),
	);
	// liste légère (sans les fichiers) : chargée avec la page pour le compteur du bouton, relue à l'ouverture du choix
	// et après un import (le flux importé en sort)
	const chargerEnAttente = React.useCallback(async () => {
		const rang = ++dernierChargement.current;
		setChargementEnAttente(true);
		try {
			const flux = await getFluxPesEnAttente(identifiantExercice);
			if (rang === dernierChargement.current) {
				setEnAttente(flux);
				setErreurEnAttente(null);
			}
		} catch (error) {
			if (rang === dernierChargement.current) {
				setErreurEnAttente(
					getMessageErreur(
						error,
						'Impossible de charger les fichiers en attente : réessayez plus tard.',
					),
				);
			}
		} finally {
			if (rang === dernierChargement.current) {
				setChargementEnAttente(false);
			}
		}
	}, [identifiantExercice]);

	React.useEffect(() => {
		if (avecExercice) {
			chargerEnAttente();
		}
	}, [avecExercice, chargerEnAttente]);

	const suiviImport = useTraitementLong(
		'Import du fichier PES',
		"L'import du fichier a échoué",
		async (job) => {
			setImport(lireCompteRendu(job));
			chargerEnAttente();
		},
	);
	const enCours = suiviAnalyse.isEnCours || suiviImport.isEnCours;
	const importable = Boolean(
		cleFichier && analyse?.importable && import_ === null,
	);
	// fichier importé : ni nouvelle analyse ni nouvel import tant qu'un autre fichier n'est pas choisi
	const importe = import_?.importable === true;
	const nombrePieces =
		typeof analyse?.resume.pieces === 'number' ? analyse.resume.pieces : null;

	// tout changement du fichier ou du service rend l'analyse précédente caduque
	const reinitialiser = () => {
		setCleFichier(null);
		setAnalyse(null);
		setImport(null);
	};

	const choisirSource = (nouvelle: SourceImportPes | null) => {
		setSource(nouvelle);
		setRefus(null);
		reinitialiser();
	};

	const analyser = () => {
		if (!source) {
			return;
		}
		reinitialiser();
		suiviAnalyse.lancer(async () => {
			const job = await analyserImportPes(source, identifiantExercice, service);
			setCleFichier(job.cleFichier);
			return job;
		});
	};

	const importer = () => {
		if (!cleFichier) {
			return;
		}
		setImport(null);
		suiviImport.lancer(async () =>
			importerPes(
				cleFichier,
				identifiantExercice,
				service,
				source instanceof File ? undefined : source?.identifiant,
			),
		);
	};

	if (!avecExercice) {
		return (
			<Segment inverted attached className="feature" style={{ height: '100%' }}>
				<Segment>
					<Message warning>
						Aucun exercice sélectionné : ouvrez l'import PES depuis un budget.
					</Message>
				</Segment>
			</Segment>
		);
	}

	return (
		<Segment inverted attached className="feature" style={{ height: '100%' }}>
			{/* le thème donne au bloc blanc la hauteur de la zone (comme aux listes) : c'est lui qui défile, pour que
			    les longs comptes rendus restent dedans */}
			<Segment style={{ height: '100%', overflowY: 'auto' }}>
				<Header as="h2">
					Import d'un fichier PES
					<Header.Subheader>
						Mandats et titres au brouillard, avec leurs pièces jointes, depuis
						un fichier PES aller ou retour (XML, ou zip de fichiers XML et de
						pièces jointes). Le fichier est d'abord analysé sans rien écrire ;
						l'import, ensuite, est tout ou rien.
					</Header.Subheader>
				</Header>

				<Form onSubmit={analyser}>
					{/* pas de label : le Dropzone JVS ne relie pas son champ ; « Analyser » attend un fichier */}
					<Form.Field>
						<Header as="h5" content="Fichier PES (.xml ou .zip)" />
						{source ? (
							<div>
								<Label basic size="large">
									<Icon
										iconSet="Lucide"
										name={source instanceof File ? 'FileText' : 'Inbox'}
										size={16}
										aria-hidden="true"
									/>{' '}
									{nomSource(source)}
									<Label.Detail>
										{taille(
											source instanceof File ? source.size : source.taille,
										)}
									</Label.Detail>
								</Label>{' '}
								<Button
									basic
									size="small"
									type="button"
									disabled={enCours}
									aria-label={`Retirer ${nomSource(source)}`}
									onClick={() => choisirSource(null)}
								>
									<Icon
										iconSet="Lucide"
										name="X"
										size={14}
										aria-hidden="true"
									/>{' '}
									Retirer
								</Button>
							</div>
						) : (
							<>
								<ZoneDepot
									accept={TYPES_ACCEPTES}
									maxSize={TAILLE_MAX}
									disabled={enCours}
									tooltip={null}
									onDrop={(fichiers) => {
										// fichier refusé : onDrop reçoit une liste vide, le motif vient de onDropRejected
										if (fichiers.length > 0) {
											choisirSource(fichiers[0]);
										}
									}}
									onDropRejected={(rejets) => setRefus(motifRefus(rejets[0]))}
								/>
								{refus && (
									<Message negative size="small" role="alert">
										{refus}
									</Message>
								)}
								<Divider horizontal>ou</Divider>
								<Button
									basic
									type="button"
									disabled={enCours}
									onClick={() => {
										chargerEnAttente();
										setChoixOuvert(true);
									}}
								>
									<Icon
										iconSet="Lucide"
										name="Inbox"
										size={14}
										aria-hidden="true"
									/>{' '}
									Choisir un fichier en attente ({enAttente.length})
								</Button>
							</>
						)}
					</Form.Field>

					<FluxEnAttenteModal
						open={choixOuvert}
						flux={enAttente}
						chargement={chargementEnAttente}
						erreur={erreurEnAttente}
						onClose={() => setChoixOuvert(false)}
						onChoisir={choisirSource}
					/>

					{isServiceVisible && (
						<>
							<Form.Field
								control={ServiceCombobox}
								label="Service par défaut"
								identifiantService={service}
								disabled={enCours}
								onSelect={(identifiant: number) => {
									setService(identifiant || undefined);
									reinitialiser();
								}}
								width={6}
							/>
							<p>
								<small>
									Posé sur les pièces du fichier sans service ; obligatoire si
									votre profil en exige un.
								</small>
							</p>
						</>
					)}

					<Button
						primary
						type="submit"
						disabled={!source || importe || enCours}
						loading={suiviAnalyse.isEnCours}
					>
						Analyser le fichier
					</Button>
					<Button
						positive
						type="button"
						disabled={!importable || enCours}
						loading={suiviImport.isEnCours}
						onClick={importer}
					>
						{nombrePieces !== null && importable
							? `Importer ${pluriel(nombrePieces, 'pièce')}`
							: 'Importer'}
					</Button>
				</Form>

				{suiviAnalyse.traitementComponent}
				{suiviImport.traitementComponent}

				{/* compte rendu sous le formulaire, dans le même bloc : un espace JVS (Divider hidden) les sépare */}
				{import_ && source && (
					<>
						<Divider hidden />
						<CompteRenduImport
							etape="import"
							compteRendu={import_}
							nomFichier={nomSource(source)}
						/>
					</>
				)}
				{analyse && source && !import_?.importable && (
					<>
						<Divider hidden />
						<CompteRenduImport
							etape="analyse"
							compteRendu={analyse}
							nomFichier={nomSource(source)}
						/>
					</>
				)}
			</Segment>
		</Segment>
	);
};

export default ImportPes;
